/* FIFI information pages: TOC, accordions, reveals, form focus. No dependencies. */
(() => {
  if (window.FifiPages) return;

  const root = document.documentElement;
  root.classList.add('fifi-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const usedIds = new Set();

  const headerOffset = () => {
    const n = parseInt(getComputedStyle(root).getPropertyValue('--fifi-header-offset'), 10);
    return Number.isNaN(n) ? 110 : n;
  };

  const scrollToEl = (el) => {
    const top = el.getBoundingClientRect().top + window.scrollY - headerOffset() - 16;
    window.scrollTo({ top, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    el.focus({ preventScroll: true });
  };

  const uniqueId = (text) => {
    const base = 'fifi-' + ((text || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section');
    let id = base;
    let i = 2;
    while (usedIds.has(id) || document.getElementById(id)) id = `${base}-${i++}`;
    usedIds.add(id);
    return id;
  };

  function initTables(scope) {
    scope.querySelectorAll('.fifi-rte table').forEach((table) => {
      if (table.parentElement.classList.contains('fifi-table-wrap')) return;
      const wrap = document.createElement('div');
      wrap.className = 'fifi-table-wrap';
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('tabindex', '0');
      wrap.setAttribute('aria-label', 'Scrollable table');
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  function initToc(scope) {
    scope.querySelectorAll('[data-fifi-toc]').forEach((toc) => {
      if (toc.dataset.ready) return;
      toc.dataset.ready = 'true';
      const source = document.getElementById(toc.dataset.source);
      const list = toc.querySelector('[data-fifi-toc-list]');
      const toggle = toc.querySelector('[data-fifi-toc-toggle]');
      const panel = toc.querySelector('[data-fifi-toc-panel]');
      const section = toc.closest('[data-fifi-policy]');
      if (!source || !list) return;

      const heads = Array.from(source.querySelectorAll('h2')).filter((h) => h.textContent.trim());
      if (heads.length < parseInt(toc.dataset.min || '2', 10)) return;

      heads.forEach((h, i) => {
        if (!h.id) h.id = uniqueId(h.textContent);
        h.setAttribute('tabindex', '-1');
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'fifi-toc__link';
        a.href = `#${h.id}`;
        const num = document.createElement('span');
        num.className = 'fifi-toc__num';
        num.setAttribute('aria-hidden', 'true');
        num.textContent = String(i + 1).padStart(2, '0');
        const label = document.createElement('span');
        label.textContent = h.textContent.trim();
        a.append(num, label);
        li.append(a);
        list.append(li);
      });

      toc.hidden = false;
      if (section) section.classList.add('fifi-policy--toc-ready');

      const setOpen = (open) => {
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
        if (panel) panel.setAttribute('data-open', String(open));
      };

      if (toggle) toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));

      toc.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && toggle && toggle.getAttribute('aria-expanded') === 'true') {
          setOpen(false);
          toggle.focus();
        }
      });

      list.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;
        e.preventDefault();
        if (!desktop.matches) setOpen(false);
        scrollToEl(target);
        history.replaceState(null, '', link.getAttribute('href'));
      });

      if ('IntersectionObserver' in window) {
        const links = new Map(Array.from(list.querySelectorAll('a')).map((a) => [a.getAttribute('href').slice(1), a]));
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((l) => l.removeAttribute('aria-current'));
            const active = links.get(entry.target.id);
            if (active) active.setAttribute('aria-current', 'true');
          });
        }, { rootMargin: `-${headerOffset() + 10}px 0px -65% 0px` });
        heads.forEach((h) => io.observe(h));
      }

      const hash = decodeURIComponent(window.location.hash.slice(1));
      if (hash) {
        const target = document.getElementById(hash);
        if (target && source.contains(target)) setTimeout(() => scrollToEl(target), 60);
      }
    });
  }

  function initAccordions(scope) {
    scope.querySelectorAll('[data-fifi-accordion-trigger]').forEach((btn) => {
      if (btn.dataset.ready) return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', () => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        if (panel) panel.setAttribute('data-open', String(open));
      });
    });
  }

  function initReveal(scope) {
    const items = scope.querySelectorAll('.fifi-reveal:not(.is-visible)');
    if (!items.length) return;
    if (designMode || reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => io.observe(el));
  }

  function initForms(scope) {
    const status = scope.querySelector('[data-fifi-focus]');
    if (status && !designMode) scrollToEl(status);

    scope.querySelectorAll('.fifi-form').forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      form.addEventListener('invalid', (e) => e.target.setAttribute('aria-invalid', 'true'), true);
      form.addEventListener('input', (e) => {
        if (e.target.getAttribute('aria-invalid') === 'true' && e.target.checkValidity()) e.target.removeAttribute('aria-invalid');
      });
      form.addEventListener('submit', () => {
        const btn = form.querySelector('[type="submit"]');
        if (btn && form.checkValidity()) btn.setAttribute('aria-busy', 'true');
      });
    });
  }

  function init(scope = document) {
    initTables(scope);
    initToc(scope);
    initAccordions(scope);
    initReveal(scope);
    initForms(scope);
  }

  window.FifiPages = { init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const btn = e.target.querySelector('[data-fifi-accordion-trigger]');
    if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click();
  });
})();
